import { type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// Re-authored from ../design-system/components/navigation/Stepper.jsx (ADR-032:
// match visual output, not the prototype's injected-CSS/UMD code).

export type Step = string | { label: ReactNode };

export interface StepperProps {
  steps: Step[];
  current: number; // 0-based active index
  className?: string;
}

type StepState = 'done' | 'active' | 'todo';

const nodeClasses: Record<StepState, string> = {
  done: 'bg-[var(--ollio-gold)] text-[var(--ollio-navy-900)] border-[var(--ollio-gold)]',
  active:
    'bg-primary text-primary-foreground border-primary ring-4 ring-[var(--ollio-navy-light)]',
  todo: 'bg-card text-muted border-border',
};

const labelClasses: Record<StepState, string> = {
  done: 'text-[var(--text-heading)]',
  active: 'text-[var(--ollio-navy)]',
  todo: 'text-muted',
};

const connectorClasses: Record<StepState, string> = {
  done: 'bg-[var(--ollio-gold)]',
  active: 'bg-border',
  todo: 'bg-border',
};

export function Stepper({ steps, current, className }: StepperProps) {
  return (
    <div className={cn('flex items-start', className)}>
      {steps.map((s, i) => {
        const label = typeof s === 'string' ? s : s.label;
        const state: StepState =
          i < current ? 'done' : i === current ? 'active' : 'todo';
        const isLast = i === steps.length - 1;

        return (
          <div
            key={i}
            className="relative flex flex-1 flex-col items-center gap-2 text-center"
            data-state={state}
            aria-current={state === 'active' ? 'step' : undefined}
          >
            {/* Connector line — hidden on the last step */}
            {!isLast && (
              <span
                data-connector
                className={cn(
                  'absolute top-4 left-1/2 h-0.5 w-full',
                  connectorClasses[state],
                )}
              />
            )}

            {/* Node circle */}
            <span
              data-node
              className={cn(
                'relative z-10 flex h-8 w-8 flex-none items-center justify-center rounded-full border-[1.5px] text-sm font-semibold transition',
                nodeClasses[state],
              )}
            >
              {state === 'done' ? (
                <Check className="h-4 w-4" aria-hidden="true" />
              ) : (
                i + 1
              )}
            </span>

            {/* Label */}
            <span className={cn('max-w-[110px] text-xs font-semibold', labelClasses[state])}>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
